import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth.api';
import { tokenStore } from '@/lib/tokenStore';
import { onUnauthorized } from '@/lib/api';
import type { AuthenticatedUser, AuthLoginResponse } from '@/types/domain';
import { toApiError } from '@/lib/errors';
import { defaultCapabilitiesFor } from '@/lib/capabilities';

type LoginArgs = { username: string; pin: string };

interface AuthContextValue {
  user: AuthenticatedUser | null;
  /**
   * Effective capability set for the logged-in user. Seeded from role
   * defaults the moment we know the user, then replaced with the
   * authoritative server set once /auth/me/capabilities resolves.
   * Never empty for an authenticated user — that was the V4.2 lock-out
   * bug where any transient failure of the capabilities endpoint hid
   * every <RequireCapability> route.
   */
  capabilities: Set<string>;
  /**
   * True after the capabilities endpoint has been called at least once
   * for this session (success OR failure). Use this to defer rendering
   * gates that need authoritative data.
   */
  capabilitiesLoaded: boolean;
  isBooting: boolean;
  isAuthenticated: boolean;
  /** True if the logged-in user has the given capability. */
  hasCapability: (capability: string) => boolean;
  login: (args: LoginArgs) => Promise<AuthLoginResponse>;
  logout: () => Promise<void>;
  /** Re-fetch capabilities (call after Permissions matrix saves). */
  refreshCapabilities: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [capabilities, setCapabilities] = useState<Set<string>>(new Set());
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState<boolean>(false);
  const [isBooting, setIsBooting] = useState<boolean>(true);
  const queryClient = useQueryClient();

  const clearAuth = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setCapabilities(new Set());
    setCapabilitiesLoaded(false);
    queryClient.clear();
  }, [queryClient]);

  // Seed capabilities immediately from role defaults so the UI is
  // functional even before /auth/me/capabilities resolves. The fetched
  // result (which includes per-user matrix overrides) replaces this.
  const seedFromRole = useCallback((me: AuthenticatedUser) => {
    setCapabilities(defaultCapabilitiesFor(me.view_type));
  }, []);

  const loadCapabilities = useCallback(async (fallback?: AuthenticatedUser) => {
    try {
      const result = await authApi.myCapabilities();
      setCapabilities(new Set(result.capabilities ?? []));
    } catch {
      // Server-side endpoint failed (network blip, deploy in flight,
      // older backend). Keep whatever capability set we already have —
      // either role defaults seeded earlier or the previous successful
      // load. If somehow we have neither, derive from the user's role
      // so the user is never locked out of the entire app.
      if (fallback) {
        setCapabilities((prev) =>
          prev.size > 0 ? prev : defaultCapabilitiesFor(fallback.view_type)
        );
      }
    } finally {
      setCapabilitiesLoaded(true);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onUnauthorized(() => {
      clearAuth();
    });
    return () => {
      unsubscribe();
    };
  }, [clearAuth]);

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      const token = tokenStore.getAccessToken();
      if (!token) {
        setIsBooting(false);
        return;
      }
      try {
        const me = await authApi.me();
        if (!mounted) return;
        setUser(me);
        seedFromRole(me);
        await loadCapabilities(me);
      } catch {
        if (mounted) {
          tokenStore.clear();
          setUser(null);
          setCapabilities(new Set());
          setCapabilitiesLoaded(false);
        }
      } finally {
        if (mounted) {
          setIsBooting(false);
        }
      }
    }
    void bootstrap();
    return () => {
      mounted = false;
    };
  }, [loadCapabilities, seedFromRole]);

  const login = useCallback(
    async ({ username, pin }: LoginArgs) => {
      try {
        const response = await authApi.login(username, pin);
        tokenStore.setAccessToken(response.access_token);
        if (response.refresh_token) {
          tokenStore.setRefreshToken(response.refresh_token);
        }
        const me = await authApi.me();
        setUser(me);
        seedFromRole(me);
        await loadCapabilities(me);
        return response;
      } catch (error) {
        throw toApiError(error);
      }
    },
    [loadCapabilities, seedFromRole]
  );

  const logout = useCallback(async () => {
    const refresh = tokenStore.getRefreshToken() ?? undefined;
    try {
      await authApi.logout(refresh);
    } catch {
      // Ignore logout errors; proceed with local cleanup.
    }
    clearAuth();
  }, [clearAuth]);

  const hasCapability = useCallback(
    (capability: string) => capabilities.has(capability),
    [capabilities]
  );

  const refreshCapabilities = useCallback(async () => {
    await loadCapabilities(user ?? undefined);
  }, [loadCapabilities, user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      capabilities,
      capabilitiesLoaded,
      isBooting,
      isAuthenticated: !!user,
      hasCapability,
      login,
      logout,
      refreshCapabilities
    }),
    [
      user,
      capabilities,
      capabilitiesLoaded,
      isBooting,
      hasCapability,
      login,
      logout,
      refreshCapabilities
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
